import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner} from 'react-bootstrap'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import axios from 'axios'
import {
  BsBoxArrowUpRight,
  BsEyeFill,
  BsFileEarmarkImage,
  BsFileEarmarkPdf,
  BsFunnelFill,
  BsPaperclip,
  BsPersonFill} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import toast from 'react-hot-toast'
import { Navigate, useLocation } from 'react-router-dom'

import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import {
  buildHomeworkHeaders,
  fetchParentStudentsHomework,
  getHomeworkErrorMessage,
  parseParentStudentsHomeworkResponse,
  resolveInstituteId} from './commonComponents/homeworkApi'
import { useInstitute } from './commonComponents/InstituteContext'

const INITIAL_FILTERS = {
  student_id: ''}

const formatDueDate = (value) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'})
}

const statusBadge = (s) => {
  const v = String(s || '').toLowerCase()
  if (v === 'published') return <Badge bg="success">Published</Badge>
  if (v === 'draft') return <Badge bg="secondary">Draft</Badge>
  if (v === 'archived') return <Badge bg="warning" text="dark">Archived</Badge>
  return <Badge bg="light" text="dark">{s || '—'}</Badge>
}

const getAttachmentKind = (url, label) => {
  const hay = `${url || ''} ${label || ''}`.toLowerCase()
  if (/\.pdf(\?|$)/i.test(hay)) return 'pdf'
  if (/\.(jpe?g|png|gif|webp)(\?|$)/i.test(hay)) return 'image'
  return 'file'
}

const openAttachment = (url) => {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

function HomeworkAttachmentCell({ row }) {
  if (!row?.attachment_url) {
    return <span className="text-muted">—</span>
  }

  const label =
    row.media_label && row.media_label !== '—' ? row.media_label : 'Attachment'
  const kind = getAttachmentKind(row.attachment_url, label)
  const Icon =
    kind === 'pdf' ? BsFileEarmarkPdf : kind === 'image' ? BsFileEarmarkImage : BsPaperclip

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {/* <span
        className="d-inline-flex align-items-center gap-2 rounded-2 px-2 py-1"
        style={{
          backgroundColor: '#F3F4F6',
          border: '1px solid #E5E7EB',
          fontSize: '12px',
          color: '#374151',
          maxWidth: '160px',
          fontFamily: 'Inter, system-ui, sans-serif'}}
        title={label}
      >
        <Icon size={16} style={{ color: '#223843', flexShrink: 0 }} />
        <span className="text-truncate">{label}</span>
      </span> */}
      <Button
        type="button"
        variant="light"
        size="sm"
        className="d-inline-flex align-items-center gap-1 border"
        style={{
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#223843',
          padding: '4px 10px'}}
        onClick={() => openAttachment(row.attachment_url)}
        title="Open attachment in new tab"
      >
        <BsBoxArrowUpRight size={14} />
        Open
      </Button>
    </div>
  )
}

export default function ParentHomework() {
  const { logout, user } = useAuth()
  const normalizedUserType = (user?.user_type || '').toLowerCase()
  const isParentUser = normalizedUserType === 'parents'
  const { isCollapsed } = useSidebar()
  const location = useLocation()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()

  const [rows, setRows] = useState([])
  const [parentStudents, setParentStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS)
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [viewTarget, setViewTarget] = useState(null)

  const loadHomework = useCallback(async (filterState) => {
    setLoading(true)
    try {
      const params = {}
      if (filterState.student_id) params.student_id = Number(filterState.student_id)
      const instituteId = resolveInstituteId()
      if (instituteId) params.institute_id = instituteId

      const res = await fetchParentStudentsHomework(params)
      setRows(parseParentStudentsHomeworkResponse(res))
    } catch (err) {
      toast.error(getHomeworkErrorMessage(err))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isParentUser) return
    loadHomework(appliedFilters)
  }, [location.pathname, location.key, appliedFilters, loadHomework, isParentUser])

  useEffect(() => {
    if (!isParentUser) return
    const loadParentStudents = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/parents/students`, {
          headers: buildHomeworkHeaders()})
        const list = Array.isArray(res?.data?.data) ? res.data.data : []
        setParentStudents(list)
      } catch {
        setParentStudents([])
      }
    }
    loadParentStudents()
  }, [isParentUser])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage
  const indexOfLastItem = currentPage * itemsPerPage
  const displayRows = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem],
  )

  useEffect(() => {
    if (rows.length === 0) return
    const tp = Math.ceil(rows.length / itemsPerPage) || 1
    if (currentPage > tp) setCurrentPage(tp)
  }, [rows.length, itemsPerPage, currentPage])

  const studentOptions = useMemo(() => {
    const map = new Map()
    parentStudents.forEach((s) => {
      const id = s.id ?? s.student_id
      const name = s.name ?? s.student_name
      if (id != null && name) map.set(String(id), name)
    })
    if (map.size === 0) {
      rows.forEach((r) => {
        if (r.student_id != null && r.student_name) {
          map.set(String(r.student_id), r.student_name)
        }
      })
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [parentStudents, rows])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const applyFilters = () => {
    setAppliedFilters({ ...filters })
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS)
    setAppliedFilters(INITIAL_FILTERS)
    setCurrentPage(1)
  }

  if (!isParentUser) {
    return <Navigate to="/Dashboard" replace />
  }

  return (
    <>
      <style>
        {`
          .page-title {
            font-family: 'Inter', system-ui, sans-serif;
            letter-spacing: -0.02em;
            color: #223843;
          }
          .table td {
            overflow: visible !important;
            user-select: text;
          }
          .table tbody tr {
            overflow: visible !important;
            cursor: default;
          }
          .table-card {
            overflow: visible !important;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
          }
          .table td, .table th { user-select: text; }
          .hw-filter-select {
            border-radius: 8px;
            border: 1px solid #D1D3D4;
            font-size: 14px;
            height: 40px;
            transition: all 0.2s ease;
            background-color: #ffffff;
            font-family: Inter, system-ui, sans-serif;
          }
          .table {
            overflow: visible !important;
          }
        `}
      </style>

      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px'}}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
                gap: '12px'}}
            >
              <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0}}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3
                    className="m-0 fw-bold page-title text-start"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: isMobile ? '14px' : 'clamp(18px, 2.5vw, 22px)',
                      lineHeight: '1.2'}}
                  >
                    Homework
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{ fontSize: isMobile ? '10px' : '13px', color: '#6b7280' }}
                  >
                    View published homework for your children.
                  </p>
                </div>
              </div>

              {!isMobile && (
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  <ProfileDropdown logout={logout} notificationCount={3} showNotifications={true} />
                </div>
              )}
            </div>
          </div>
        </div>

        <Container
          fluid
          className="p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span
                  style={{
                    color: '#6b7280',
                    fontSize: '13px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value, 10))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>
              <button
                type="button"
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'}}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          <div
            style={{
              maxHeight: showFilterCard ? '400px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card style={{ border: '1px solid #D1D3D4', borderRadius: '12px' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #F3F4F6' }}>
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#223843',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'}}
                    >
                      <BsPersonFill style={{ color: '#ffffff', fontSize: '18px' }} />
                    </div>
                    <div className="text-start">
                      <h5 className="m-0 fw-semibold" style={{ fontSize: '16px', color: '#111827' }}>
                        Filter by child
                      </h5>
                      <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                        Show homework for one of your linked students
                      </p>
                    </div>
                  </div>
                </div>
                <Card.Body>
                  <Row className="g-3 text-start">
                    <Col xs={12} md={6} lg={4}>
                      <Form.Label>Student</Form.Label>
                      <Form.Select
                        name="student_id"
                        value={filters.student_id}
                        onChange={handleFilterChange}
                        className="hw-filter-select"
                      >
                        <option value="">All children</option>
                        {studentOptions.map(([id, name]) => (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end gap-2 mt-3 flex-wrap">
                    <Button variant="outline-secondary" onClick={resetFilters} style={{ borderRadius: '8px' }}>
                      Reset
                    </Button>
                    <Button
                      onClick={applyFilters}
                      style={{
                        backgroundColor: '#223843',
                        border: 'none',
                        borderRadius: '8px',
                        minWidth: '100px'}}
                    >
                      Apply
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>

          <Card
            className="table-card"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'visible',
              backgroundColor: '#ffffff'}}
          >
            <div className="table-wrapper">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span className="text-muted" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Loading homework…
                  </span>
                </div>
              ) : rows.length === 0 ? (
                <div
                  className="text-center text-muted py-5 px-3"
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  {appliedFilters.student_id
                    ? 'No published homework found for the selected child.'
                    : 'No published homework found for your linked students.'}
                </div>
              ) : (
                <Table className="table mb-0">
                  <Thead>
                    <Tr style={{ backgroundColor: '#F3F4F6' }}>
                      {[
                        'Student',
                        'Admission No.',
                        'Title',
                        'Class',
                        'Subject',
                        'Due date',
                        'Attachment',
                        'Status',
                        'Action',
                      ].map((col) => (
                        <Th
                          key={col}
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            padding: '16px',
                            border: 'none',
                            borderBottom: '1px solid #D1D3D4',
                            textAlign: col === 'Action' ? 'center' : 'left',
                            color: '#111827',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            whiteSpace: col === 'Action' ? 'nowrap' : undefined}}
                        >
                          {col}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody className="text-start">
                    {displayRows.map((r, index) => (
                      <Tr
                        key={`${r.student_id}-${r.id}`}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                          borderBottom: '1px solid #E5E7EB'}}
                      >
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {r.student_name || '—'}
                        </Td>
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {r.admission_number || '—'}
                        </Td>
                        <Td style={{ padding: '16px', fontWeight: 600, minWidth: '140px' }}>
                          {r.title}
                        </Td>
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {r.class_name_only || '—'}
                        </Td>
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {r.subject_name || '—'}
                        </Td>
                        <Td style={{ padding: '16px', whiteSpace: 'nowrap' }}>
                          {formatDueDate(r.due_date)}
                        </Td>
                        <Td style={{ padding: '16px', minWidth: '200px' }}>
                          <HomeworkAttachmentCell row={r} />
                        </Td>
                        <Td style={{ padding: '16px' }}>{statusBadge(r.status)}</Td>
                        <Td className="text-center" style={{ padding: '16px' }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: '#223843',
                              color: '#fff',
                              borderRadius: '50%',
                              width: 36,
                              height: 36}}
                            onClick={() => setViewTarget(r)}
                            title="View homework"
                          >
                            <BsEyeFill size={14} />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </div>
          </Card>

          {!loading && rows.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              totalItems={rows.length}
              itemLabel="homework entries"
              className="d-flex justify-content-between align-items-center gap-3 mb-3 mt-4 flex-wrap"
            />
          )}
        </Container>
      </div>

      <Modal show={Boolean(viewTarget)} onHide={() => setViewTarget(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            {viewTarget?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-start">
          <p>
            <strong>Student:</strong> {viewTarget?.student_name || '—'}
          </p>
          <p>
            <strong>Admission no.:</strong> {viewTarget?.admission_number || '—'}
          </p>
          <p>
            <strong>Class:</strong> {viewTarget?.class_name_only || viewTarget?.class_name || '—'}
          </p>
          <p>
            <strong>Subject:</strong> {viewTarget?.subject_name || '—'}
          </p>
          <p>
            <strong>Teacher:</strong> {viewTarget?.teacher_name || '—'}
          </p>
          <p>
            <strong>Status:</strong> {viewTarget?.status}
          </p>
          <p>
            <strong>Due date:</strong> {formatDueDate(viewTarget?.due_date)}
          </p>
          <p>
            <strong>Description:</strong>
            <br />
            {viewTarget?.description || '—'}
          </p>
          <div className="mb-0">
            <strong className="d-block mb-2">Attachment</strong>
            {viewTarget?.attachment_url ? (
              <HomeworkAttachmentCell row={viewTarget} />
            ) : (
              '—'
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}
